'use client';

import { useEffect, useState } from 'react';

export type ConsentCategory = 'necessary' | 'preferences' | 'statistics' | 'marketing';

/** Sentinel meaning "no consent required" — content renders unconditionally. */
export const NO_CONSENT_REQUIRED = 'none';

export const CONSENT_CATEGORIES: ConsentCategory[] = [
  'necessary',
  'preferences',
  'statistics',
  'marketing',
];

type CookiebotWindow = Window & {
  Cookiebot?: { consent?: Partial<Record<ConsentCategory, boolean>> };
};

/** True once Cookiebot has exposed a consent snapshot (accepted, declined, or prior visit). */
export const isCookiebotConsentReady = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (window as CookiebotWindow).Cookiebot?.consent !== undefined;
};

/**
 * Becomes true when Cookiebot consent is available. Avoids firing tracking before
 * marketing/statistics consent is known (which would cause a second fire on accept).
 */
export const useCookiebotReady = (): boolean => {
  const [ready, setReady] = useState(() => isCookiebotConsentReady());

  useEffect(() => {
    if (ready) return;

    const update = () => {
      if (isCookiebotConsentReady()) {
        setReady(true);
      }
    };

    update();
    window.addEventListener('CookiebotOnConsentReady', update);
    window.addEventListener('CookiebotOnAccept', update);
    window.addEventListener('CookiebotOnDecline', update);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', update);
      window.removeEventListener('CookiebotOnAccept', update);
      window.removeEventListener('CookiebotOnDecline', update);
    };
  }, [ready]);

  return ready;
};

/** Read the current Cookiebot consent for a category. Returns false until consent is known. */
export const hasCookiebotConsent = (category: ConsentCategory): boolean => {
  if (typeof window === 'undefined') return false;
  return (window as CookiebotWindow).Cookiebot?.consent?.[category] === true;
};

const normalizeCategory = (category?: string | null): ConsentCategory | null => {
  const value = category?.toLowerCase();
  if (!value || value === NO_CONSENT_REQUIRED) return null;
  return CONSENT_CATEGORIES.includes(value as ConsentCategory) ? (value as ConsentCategory) : null;
};

/**
 * Reactively tracks whether the visitor has granted consent for `category`.
 *
 * Returns `true` when no category is required (`undefined`/`'none'`/unknown), otherwise the
 * live Cookiebot consent state — updated on Cookiebot's consent lifecycle events so gated
 * content appears/disappears when the visitor accepts or withdraws consent (no reload needed).
 */
export const useCookiebotConsent = (category?: string | null): boolean => {
  const required = normalizeCategory(category);
  const [granted, setGranted] = useState<boolean>(() =>
    required ? hasCookiebotConsent(required) : true,
  );

  useEffect(() => {
    if (!required) {
      setGranted(true);
      return;
    }

    const update = () => setGranted(hasCookiebotConsent(required));
    update();

    window.addEventListener('CookiebotOnConsentReady', update);
    window.addEventListener('CookiebotOnAccept', update);
    window.addEventListener('CookiebotOnDecline', update);

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', update);
      window.removeEventListener('CookiebotOnAccept', update);
      window.removeEventListener('CookiebotOnDecline', update);
    };
  }, [required]);

  return granted;
};
